import React, { useState, useRef, useEffect } from "react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

// Custom-date-filter: date-only range picker for the dashboard. Reuses react-date-range (same library
// as the Reports/Calls picker) but with a 1-year cap and no time-of-day inputs, per the agreed spec.
const DAY_MS = 86400000;
const MAX_DAYS = 366; // ~1 year cap

const CustomDateRangePopover = ({ initialRange, onApply, onClose }) => {
  const [range, setRange] = useState([
    {
      startDate: initialRange && initialRange.fromDate != null ? new Date(initialRange.fromDate) : new Date(Date.now() - 7 * DAY_MS),
      endDate: initialRange && initialRange.toDate != null ? new Date(initialRange.toDate) : new Date(),
      key: "selection"
    }
  ]);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  // one year ago (leap-safe); structurally caps selection at 1 year back so the calendar can't
  // scroll/select earlier years — enforces the "max range = 1 year" rule.
  const minDate = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  const sel = range[0] || {};
  const days = sel.startDate && sel.endDate
    ? Math.round((new Date(sel.endDate).setHours(0, 0, 0, 0) - new Date(sel.startDate).setHours(0, 0, 0, 0)) / DAY_MS) + 1
    : 0;
  const exceedsMax = days > MAX_DAYS;
  const canApply = !!sel.startDate && !!sel.endDate && !exceedsMax;

  const handleApply = () => {
    if (!canApply) return;
    const from = new Date(sel.startDate);
    from.setHours(0, 0, 0, 0);
    const to = new Date(sel.endDate);
    to.setHours(23, 59, 59, 999);
    onApply({ fromDate: from.getTime(), toDate: to.getTime() });
  };

  return (
    <div className="customDatePopover" ref={ref}>
      <DateRange
        editableDateInputs={false}
        onChange={item => setRange([item.selection])}
        moveRangeOnFirstSelection={false}
        ranges={range}
        minDate={minDate}
        maxDate={new Date()}
      />
      {exceedsMax && <p className="customDateErr">Date range is limited to 1 year</p>}
      <div className="customDateFooter">
        <button type="button" className="customDateCancelBtn" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="customDateApplyBtn" onClick={handleApply} disabled={!canApply}>
          Apply
        </button>
      </div>
    </div>
  );
};

export default CustomDateRangePopover;
